/**
 * Per-country national-ID number formatting — the single source of truth the
 * web UI's live-typing formatter and (optionally) the API's validation both
 * build on, so the shape the UI shows can never drift from what the OCR
 * extractor recognizes.
 *
 * The OCR provider (`packages/api/.../ocr-countries.ts`) holds the *matching*
 * side: regexes that recognize a COMPLETE printed ID string per country
 * (`DOCUMENTO_FORMATS`). This module holds the *formatting* side: given a raw
 * (possibly partial, as-you-type) string, group it into that country's
 * canonical printed shape. The two are derived from the same per-country
 * knowledge; keep them in sync when either changes.
 *
 * Design rules that hold for EVERY country:
 *   1. Uppercase; strip anything outside `[0-9A-Z]` and that country's own
 *      separator characters.
 *   2. **Honor separators already present.** If the cleaned input already
 *      contains the country's separator, we do NOT regroup it — we only
 *      collapse duplicate separators and return. This preserves values the OCR
 *      already grouped and lets legitimate variable-width forms survive
 *      (e.g. Panama provinces 10–13, letter prefixes). Only a *bare run* (no
 *      separators) gets the canonical grouping applied.
 *   3. Never throw. Empty in → empty out. An unknown/unsupported ISO3 falls
 *      back to a generic clean-and-cap passthrough (no grouping).
 *
 * Live-typing caveat: for countries whose number carries a trailing check
 * char split by a separator (CHL/URY/PER/SLV/BRA/NIC), a *bare* run is
 * inherently ambiguous mid-type (is the last digit a check or body?). We split
 * the check only at/after the length where it is unambiguous per the country's
 * real format; before that we group the body only. In practice the honor-
 * separators rule (1) carries real inputs, since scanned/typed values usually
 * already contain the separator.
 */
/**
 * The 17 countries whose national-ID printed format is modeled here — the same
 * set the OCR extractor's `DOCUMENTO_FORMATS` covers. Panamá first (the default
 * for this Ley-23 product), then the rest alphabetically by Spanish name.
 */
export const SUPPORTED_ID_COUNTRIES = [
    { iso3: 'PAN', labelEs: 'Panamá' },
    { iso3: 'ARG', labelEs: 'Argentina' },
    { iso3: 'BOL', labelEs: 'Bolivia' },
    { iso3: 'BRA', labelEs: 'Brasil' },
    { iso3: 'CHL', labelEs: 'Chile' },
    { iso3: 'COL', labelEs: 'Colombia' },
    { iso3: 'CRI', labelEs: 'Costa Rica' },
    { iso3: 'ECU', labelEs: 'Ecuador' },
    { iso3: 'SLV', labelEs: 'El Salvador' },
    { iso3: 'GTM', labelEs: 'Guatemala' },
    { iso3: 'HND', labelEs: 'Honduras' },
    { iso3: 'MEX', labelEs: 'México' },
    { iso3: 'NIC', labelEs: 'Nicaragua' },
    { iso3: 'PRY', labelEs: 'Paraguay' },
    { iso3: 'PER', labelEs: 'Perú' },
    { iso3: 'URY', labelEs: 'Uruguay' },
    { iso3: 'VEN', labelEs: 'Venezuela' },
];
const SPECS = {
    // Cédula PROVINCIA-TOMO-FOLIO; letter prefixes PE/E/N — exact legacy shape.
    PAN: { style: 'panama', validLen: [4, 14] },
    // Cédula de Ciudadanía / NUIP: dot-grouped by thousands.
    COL: { style: 'thousands', validLen: [6, 10] },
    // DNI: dot-grouped by thousands.
    ARG: { style: 'thousands', validLen: [7, 8] },
    // Cédula: dot-grouped by thousands.
    PRY: { style: 'thousands', validLen: [6, 8] },
    // Cédula: V/E type letter + dot-grouped body.
    VEN: { style: 'thousands', prefixes: ['V', 'E'], validLen: [6, 9] },
    // RUN/RUT: dot-grouped body + '-' + mod-11 check (0–9 or K). Full form ≥9.
    CHL: { style: 'thousands', check: { len: 1, sep: '-', minTotal: 9 }, validLen: [7, 9] },
    // Cédula: dot-grouped 7-digit body + '-' + check digit. Full form ≥8.
    URY: { style: 'thousands', check: { len: 1, sep: '-', minTotal: 8 }, validLen: [8, 8] },
    // Cédula: PROVINCIA(1)-TOMO(4)-FOLIO(4).
    CRI: { style: 'fixed', groups: [1, 4, 4], sep: '-', validLen: [9, 10] },
    // DNI: 4-4-5.
    HND: { style: 'fixed', groups: [4, 4, 5], sep: '-', validLen: [13, 13] },
    // DPI/CUI: 4-5-4, space-separated.
    GTM: { style: 'fixed', groups: [4, 5, 4], sep: ' ', validLen: [13, 13] },
    // CPF: 3.3.3-2.
    BRA: { style: 'fixed', groups: [3, 3, 3], sep: '.', check: { len: 2, sep: '-', minTotal: 11 }, validLen: [11, 11] },
    // DUI: 8 digits + '-' + 1 check digit.
    SLV: { style: 'fixed', groups: [8], sep: '-', check: { len: 1, sep: '-', minTotal: 9 }, validLen: [9, 9] },
    // DNI: 8 digits + optional '-' + 1 administrative check digit.
    PER: { style: 'fixed', groups: [8], sep: '-', check: { len: 1, sep: '-', minTotal: 9 }, validLen: [8, 9] },
    // Cédula: 3-6-4 + trailing check LETTER (concatenated to the last group).
    NIC: { style: 'fixed', groups: [3, 6, 4], sep: '-', check: { len: 1, sep: '', minTotal: 14 }, validLen: [14, 14] },
    // CURP / Clave de Elector: 18 structured alnum, no separators.
    MEX: { style: 'plain', maxLen: 18, validLen: [10, 18] },
    // Cédula: 10 digits, no separators.
    ECU: { style: 'plain', maxLen: 10, validLen: [10, 10] },
    // Cédula: 6–8-digit run (complemento rare — left bare), no separators.
    BOL: { style: 'plain', maxLen: 11, validLen: [6, 11] },
};
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Escape a single char for use in a RegExp character/pattern. */
function esc(ch) {
    return ch.replace(/[.*+?^${}()|[\]\\ ]/g, '\\$&');
}
/** The separator characters a given spec uses (for cleaning + honor-existing). */
function sepChars(spec) {
    switch (spec.style) {
        case 'panama':
            return '-';
        case 'thousands':
            // '.' always; '-' whenever a prefix or check can appear.
            return spec.prefixes || spec.check ? '.-' : '.';
        case 'fixed': {
            const s = new Set([spec.sep, ...(spec.check ? [spec.check.sep] : [])].filter((c) => c.length > 0));
            return [...s].join('');
        }
        case 'plain':
            return '';
    }
}
/** Uppercase and strip anything outside `[0-9A-Z]` and the allowed separators. */
function clean(raw, seps) {
    const cls = seps ? [...seps].map(esc).join('') : '';
    return raw.toUpperCase().replace(new RegExp(`[^0-9A-Z${cls}]`, 'g'), '');
}
/** Bare alnum core: the cleaned string with every separator removed. */
function core(cleaned, seps) {
    if (!seps)
        return cleaned;
    const cls = [...seps].map(esc).join('');
    return cleaned.replace(new RegExp(`[${cls}]`, 'g'), '');
}
/**
 * If the cleaned input already carries any separator, honor it: collapse runs
 * of each separator to one and return. `null` when there are no separators
 * (caller should apply canonical grouping instead).
 */
function honorExisting(cleaned, seps) {
    if (!seps || ![...seps].some((c) => cleaned.includes(c)))
        return null;
    let out = cleaned;
    for (const c of seps)
        out = out.replace(new RegExp(`${esc(c)}{2,}`, 'g'), c);
    return out;
}
/** Group a digit string by 3 from the right, dot-separated. */
function groupThousands(digits) {
    if (digits.length <= 3)
        return digits;
    const head = digits.length % 3 || 3;
    const parts = [digits.slice(0, head)];
    for (let i = head; i < digits.length; i += 3)
        parts.push(digits.slice(i, i + 3));
    return parts.join('.');
}
/** Fill fixed group widths left-to-right; any leftover joins the last group. */
function groupFixed(body, groups, sep) {
    const parts = [];
    let i = 0;
    for (const g of groups) {
        if (i >= body.length)
            break;
        parts.push(body.slice(i, i + g));
        i += g;
    }
    if (i < body.length) {
        if (parts.length > 0)
            parts[parts.length - 1] += body.slice(i);
        else
            parts.push(body.slice(i));
    }
    return parts.join(sep);
}
/** Split a trailing check off a core, per spec (only once it's unambiguous). */
function splitCheck(c, check) {
    if (!check || c.length < check.minTotal)
        return { body: c, chk: '' };
    return { body: c.slice(0, c.length - check.len), chk: c.slice(c.length - check.len) };
}
// ---------------------------------------------------------------------------
// Panama (verbatim legacy behavior)
// ---------------------------------------------------------------------------
function formatPanama(raw) {
    const v = raw.toUpperCase().replace(/[^0-9A-Z-]/g, '');
    if (v.includes('-'))
        return v.replace(/-{2,}/g, '-');
    const m = v.match(/^([A-Z]{1,3})?(\d{0,1})(\d{0,4})(\d{0,6})$/);
    if (!m)
        return v.slice(0, 14);
    const [, prefix, a, b, c] = m;
    const parts = [a, b, c].filter((x) => x && x.length > 0);
    return [prefix, ...parts].filter(Boolean).join('-');
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/** Generic fallback for an unsupported ISO3: uppercase, keep digits/letters/dash, cap. */
function formatGeneric(raw) {
    return raw.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 20);
}
/**
 * Format a national-ID number for the given country as the user types.
 * See the module doc for the universal rules. Unknown/unsupported `iso3`
 * falls back to a generic clean-and-cap.
 */
export function formatIdNumber(iso3, raw) {
    const spec = SPECS[iso3?.toUpperCase?.() ?? ''];
    if (!spec)
        return formatGeneric(raw);
    if (spec.style === 'panama')
        return formatPanama(raw);
    const seps = sepChars(spec);
    const cleaned = clean(raw, seps);
    const honored = honorExisting(cleaned, seps);
    if (honored !== null)
        return honored;
    const c = core(cleaned, seps);
    if (c.length === 0)
        return '';
    switch (spec.style) {
        case 'thousands': {
            let prefix = '';
            let rest = c;
            if (spec.prefixes) {
                const pm = rest.match(/^[A-Z]+/);
                if (pm) {
                    prefix = pm[0].slice(0, 2);
                    rest = rest.slice(pm[0].length);
                }
            }
            const { body, chk } = splitCheck(rest, spec.check);
            let out = groupThousands(body);
            if (chk)
                out += (spec.check?.sep ?? '-') + chk;
            if (prefix)
                out = out.length > 0 ? `${prefix}-${out}` : prefix;
            return out;
        }
        case 'fixed': {
            const { body, chk } = splitCheck(c, spec.check);
            let out = groupFixed(body, spec.groups, spec.sep);
            if (chk)
                out += (spec.check?.sep ?? '') + chk;
            return out;
        }
        case 'plain':
            return c.slice(0, spec.maxLen);
    }
}
// ---------------------------------------------------------------------------
// ISO alpha-2 ↔ alpha-3 bridge (for nationality fields keyed by alpha-2, e.g.
// the risk-country catalog) and canonical example placeholders.
// ---------------------------------------------------------------------------
/**
 * ISO 3166-1 alpha-2 (lowercase) → alpha-3 map, covering only the 17
 * supported ID countries. Self-contained by design — no import from
 * `risk-score.ts` — so callers can bridge a `nacionalidad` field (alpha-2,
 * e.g. the risk-country catalog) into this module's alpha-3 keys.
 */
const ISO2_TO_ISO3 = {
    pa: 'PAN',
    ar: 'ARG',
    bo: 'BOL',
    br: 'BRA',
    cl: 'CHL',
    co: 'COL',
    cr: 'CRI',
    ec: 'ECU',
    sv: 'SLV',
    gt: 'GTM',
    hn: 'HND',
    mx: 'MEX',
    ni: 'NIC',
    py: 'PRY',
    pe: 'PER',
    uy: 'URY',
    ve: 'VEN',
};
/**
 * Map a lowercase ISO 3166-1 alpha-2 code to its alpha-3 equivalent, but only
 * for the 17 countries this module models (`SUPPORTED_ID_COUNTRIES`). Case-
 * insensitive; returns `undefined` for anything unsupported or empty so
 * callers can fall back (e.g. to Panama). Never throws.
 */
export function iso2ToIso3(iso2) {
    return ISO2_TO_ISO3[iso2?.toLowerCase?.() ?? ''];
}
/** Canonical example ID string per country, correctly grouped per `SPECS`. */
const ID_EXAMPLES = {
    PAN: '8-123-4567',
    ARG: '12.345.678',
    BOL: '1234567',
    BRA: '123.456.789-09',
    CHL: '12.345.678-5',
    COL: '1.234.567',
    CRI: '1-2345-6789',
    ECU: '1712345678',
    SLV: '12345678-9',
    GTM: '1234 56789 0101',
    HND: '0801-1990-12345',
    MEX: 'PEGA900517HDFRLN08',
    NIC: '123-456789-0001A',
    PRY: '1.234.567',
    PER: '12345678-9',
    URY: '1.234.567-8',
    VEN: 'V-12.345.678',
};
/**
 * A canonical, correctly-grouped example ID string for the given country —
 * suitable as an input placeholder. Unknown/unsupported `iso3` (normalized
 * case-insensitively) falls back to Panama's example, since this is a
 * Panama-first product. Never throws.
 */
export function idNumberExample(iso3) {
    return ID_EXAMPLES[iso3?.toUpperCase?.() ?? ''] ?? ID_EXAMPLES.PAN;
}
/**
 * Soft validity hint for a country's ID number — used ONLY for a gentle UI
 * nudge, never to hard-block input (mirrors the forgiving-secretary
 * philosophy). Checks that the separator-stripped core length is within a
 * forgiving range for that country. Unknown `iso3` → any core of length ≥ 4.
 */
export function looksValidIdNumber(iso3, raw) {
    const spec = SPECS[iso3?.toUpperCase?.() ?? ''];
    if (!spec) {
        const generic = raw.toUpperCase().replace(/[^0-9A-Z]/g, '');
        return generic.length >= 4;
    }
    const c = core(clean(raw, sepChars(spec)), sepChars(spec));
    const [min, max] = spec.validLen;
    return c.length >= min && c.length <= max;
}
//# sourceMappingURL=id-formats.js.map